import { useEffect, useRef } from "react";
import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

const bridge = registerPlugin<{ setBackHandling(options: { intercept: boolean }): Promise<void>; addListener(name: string, callback: () => void): Promise<PluginListenerHandle> }>("HostedOriginGuard");
const actions: { action: () => void; priority: number }[] = [];
function syncGesture() {
  if (Capacitor.getPlatform() === "ios" && Capacitor.isPluginAvailable("HostedOriginGuard")) {
    // Older shells do not have this method; keep their existing navigation working.
    void bridge.setBackHandling({ intercept: actions.length > 0 }).catch(() => {});
  }
}
export function registerBackAction(action: () => void, priority = 10) {
  const entry = { action, priority };
  actions.push(entry); syncGesture();
  return () => { const index = actions.indexOf(entry); if (index >= 0) actions.splice(index, 1); syncGesture(); };
}
export function handleLocalBack(): boolean {
  const top = actions.reduce<typeof actions[number] | undefined>((a, b) => !a || b.priority >= a.priority ? b : a, undefined);
  if (!top) return false;
  top.action(); return true;
}
/** Local overlays/steps take precedence over route history. A busy action may
 * intentionally consume Back without abandoning an upload or save. */
export function useBackAction(enabled: boolean, action: () => void, priority = 10) {
  const latest = useRef(action); latest.current = action;
  useEffect(() => enabled ? registerBackAction(() => latest.current(), priority) : undefined, [enabled, priority]);
}
export async function watchSystemNavigation(): Promise<() => void> {
  const handles: PluginListenerHandle[] = [];
  const escape = (event: KeyboardEvent) => { if (event.key === "Escape" && handleLocalBack()) event.preventDefault(); };
  window.addEventListener("keydown", escape);
  try {
    if (Capacitor.getPlatform() === "android") {
      const { App } = await import("@capacitor/app");
      handles.push(await App.addListener("backButton", ({ canGoBack }) => {
        if (handleLocalBack()) return;
        if (canGoBack) window.history.back();
        else void App.minimizeApp();
      }));
    } else if (Capacitor.getPlatform() === "ios" && Capacitor.isPluginAvailable("HostedOriginGuard")) {
      handles.push(await bridge.addListener("navigationBack", () => { handleLocalBack(); }));
      syncGesture();
    }
  } catch { /* An older native shell still supports normal on-screen Back. */ }
  return () => { window.removeEventListener("keydown", escape); handles.forEach(handle => { void handle.remove(); }); };
}
